/**
 * The Echo Engine copy that `content/ui-strings.ts` does not cover.
 *
 * Portal chrome — navigation, the download panel, error states — lives there,
 * per product, and this file deliberately does not restate any of it. Two things
 * are left over:
 *
 * - The sign-in form. It sits outside the portal shell, and it is bilingual
 *   because a developer picks a language in Settings before they ever hold a
 *   credential, whereas EchoPrint's login is English-only.
 * - The region switcher, which has no EchoPrint counterpart at all.
 *
 * Pure and free of `server-only`: client components import this directly rather
 * than receiving strings as props.
 */

import type { ContentLocale } from '../content/constants';

export interface EngineStrings {
  signInTitle: string;
  signInSubtitle: string;
  username: string;
  password: string;
  signIn: string;
  signInFailed: string;
  offline: string;
  usernameRequired: string;
  usernameMalformed: string;
  passwordRequired: string;
  togglePassword: string;

  regionLabel: string;
  regionHelp: string;
  regionCurrent: string;

  /** Heading over the copyable command block; EchoPrint's DMG has no equivalent. */
  installSteps: string;
}

const EN: EngineStrings = {
  signInTitle: 'Echo Engine',
  signInSubtitle: 'Sign in with your developer credentials.',
  username: 'Username',
  password: 'Password',
  signIn: 'Sign in',
  signInFailed: 'Sign in failed. Try again.',
  offline: 'Could not reach the server. Check your connection and try again.',
  usernameRequired: 'Enter your username.',
  usernameMalformed: 'Usernames are 2–63 characters: letters, digits, dot, underscore or hyphen.',
  passwordRequired: 'Enter your password.',
  togglePassword: 'Toggle password visibility',

  regionLabel: 'Region',
  regionHelp: 'Switch to the deployment nearest you.',
  regionCurrent: 'Current',

  installSteps: 'Install',
};

const ZH_HANS: EngineStrings = {
  signInTitle: 'Echo Engine',
  signInSubtitle: '请使用开发者凭据登录。',
  username: '用户名',
  password: '密码',
  signIn: '登录',
  signInFailed: '登录失败，请重试。',
  offline: '无法连接服务器。请检查网络后重试。',
  usernameRequired: '请输入用户名。',
  usernameMalformed: '用户名为 2–63 个字符：字母、数字、点、下划线或连字符。',
  passwordRequired: '请输入密码。',
  togglePassword: '切换密码可见性',

  regionLabel: '区域',
  regionHelp: '切换到离你最近的部署。',
  regionCurrent: '当前',

  installSteps: '安装',
};

export const ENGINE_STRINGS: Record<ContentLocale, EngineStrings> = {
  en: EN,
  'zh-Hans': ZH_HANS,
};

export function engineStrings(locale: ContentLocale): EngineStrings {
  return ENGINE_STRINGS[locale] ?? EN;
}
